/**
 * Shared by the page's server-side `metadata` / page render. Kept outside the
 * `'use client'` content module so the server can read it for og:description.
 *
 * Each line stays up until that session's start (ET), then rolls to the next.
 * Ads can force a session with `?date=<id>` (e.g. `?date=10-15`).
 * Call only on the server (or in tests with an injected `now`) — pass the
 * resulting string into the client as a prop to avoid hydration mismatches.
 *
 * Paid traffic uses `/equity-webinar-paid` (same UI, distinct CIO slug) so CRM
 * segments don't depend on submit date.
 */
export const EQUITY_WEBINAR_SLUG = 'equity-webinar'
export const EQUITY_WEBINAR_PAID_SLUG = 'equity-webinar-paid'

const WEBINAR_LEAD_MAGNET_SLUGS = [EQUITY_WEBINAR_SLUG, EQUITY_WEBINAR_PAID_SLUG] as const
export type WebinarLeadMagnetSlug = (typeof WEBINAR_LEAD_MAGNET_SLUGS)[number]

export const isWebinarLeadMagnetSlug = (value: string): value is WebinarLeadMagnetSlug =>
  (WEBINAR_LEAD_MAGNET_SLUGS as readonly string[]).includes(value)

const SESSIONS = [
  { id: '9-24', startsAt: '2026-09-24T12:00:00-04:00', line: 'Thursday, September 24 · 12:00–12:30pm ET' },
  { id: '10-1', startsAt: '2026-10-01T12:00:00-04:00', line: 'Thursday, October 1 · 12:00–12:30pm ET' },
  { id: '10-15', startsAt: '2026-10-15T12:00:00-04:00', line: 'Thursday, October 15 · 12:00–12:30pm ET' },
] as const

export type SessionId = (typeof SESSIONS)[number]['id']

/** Whitelist `?date=` values so junk query strings fall through to the roll. */
export const parseSessionDateParam = (value: string | string[] | undefined): SessionId | undefined => {
  const raw = Array.isArray(value) ? value[0] : value
  return SESSIONS.find((s) => s.id === raw)?.id
}

export const getEventLine = (now = new Date(), sessionId?: SessionId) => {
  if (sessionId) {
    const forced = SESSIONS.find((s) => s.id === sessionId)
    if (forced) return forced.line
  }

  const t = now.getTime()
  for (const session of SESSIONS) {
    if (t < Date.parse(session.startsAt)) return session.line
  }
  return SESSIONS[2].line
}
